import { buildAiEditorPayload } from "./aiCredentialPayload";
import { credentialOptionsForModel, credentialOptionsForPurpose, initialTemplateValues, runtimeDefaultsFromTemplate } from "./agentTemplateInstantiation";
import { buildKnowledgePayload, defaultKnowledgeConfiguration, knowledgeConfigurationFromPrompt } from "./promptKnowledge";
import { getAiModelParameterConfig, supportsAiModelParameter } from "./aiModelCatalog";
import { instructionCompositionStale, instructionValuesKey, instructionVariableErrors } from "./agentInstructions";
import { emptySmartPrompt, escapeSmartText, smartPromptErrors, templateSmartPrompt } from "./smartPrompt";

export const VOICE_MODEL_ID = "gpt-4.1-mini";
export const agentEditorForm = ({ prompt, template, models = [], credentials = [] } = {}) => {
  const origin = prompt?.origin;
  const version = template?.publishedVersion;
  const runtime = runtimeDefaultsFromTemplate(template, models);
  const model = prompt?.model || runtime.model;
  const values = initialTemplateValues(origin?.variables || version?.variables || [], origin?.values || {});
  return {
    name: prompt?.name || version?.name || "",
    prompt: prompt?.prompt || "",
    promptDocument: prompt?.promptDocument || (template ? templateSmartPrompt(version)
      : origin?.instructionsMode === "template" && origin?.instructions
        ? { version: 1, text: origin.instructions, variables: origin.variables || [], values: origin.values || {} }
        : { ...emptySmartPrompt(), text: escapeSmartText(prompt?.prompt || "") }),
    values,
    composedValuesKey: origin ? instructionValuesKey(values) : "",
    instructionsMode: origin?.instructionsMode || (template ? "template" : "custom"),
    customInstructions: origin?.instructionsMode === "custom" ? prompt.prompt : "",
    generatedPrompt: origin?.generatedPrompt || (origin ? prompt.prompt : "") || "",
    confirmReplaceCustomInstructions: false,
    model,
    credentialId: prompt ? prompt.credentialId || "" : credentialOptionsForModel(credentials, models, model)[0]?.id || "",
    queueId: prompt?.queueId || "",
    maxTokens: prompt?.maxTokens ?? runtime.maxTokens,
    temperature: prompt?.temperature ?? runtime.temperature,
    maxMessages: prompt?.maxMessages ?? runtime.maxMessages,
    voice: prompt?.voice || "texto",
    voiceCredentialId: prompt?.voiceCredentialId || "",
    voiceRegion: prompt?.voiceRegion || "",
    knowledge: prompt ? knowledgeConfigurationFromPrompt(prompt, origin?.knowledgeRequirement) : defaultKnowledgeConfiguration(version?.knowledgeRequirement),
  };
};

export const agentEditorErrors = ({ form, origin, models = [], credentials = [], queues = [], bases = [], companyName = "", companyValues = [], currentModelId = "" }) => {
  const errors = [];
  const add = (field, tab, message) => { if (!errors.some(error => error.field === field)) errors.push({ field, tab, message }); };
  const model = models.find(item => item.modelId === form.model);
  if (form.name.trim().length < 5 || form.name.trim().length > 100) add("name", 0, "Informe um nome entre 5 e 100 caracteres.");
  if (form.promptDocument) {
    smartPromptErrors(form.promptDocument, companyName, 50, companyValues).forEach(error => add("promptDocument", 0, error.message));
  } else if (origin) {
    errors.push(...instructionVariableErrors(origin.variables, form.values));
    if (form.instructionsMode === "template" && instructionCompositionStale(form)) add("composition", 0, "Atualize as instruções para refletir os campos do template antes de salvar.");
  }
  const instructions = origin ? form.customInstructions : form.prompt;
  if (!form.promptDocument && (!origin || form.instructionsMode === "custom") && (instructions.trim().length < 50 || instructions.length > 20000)) add(origin ? "customInstructions" : "prompt", 0, "Use entre 50 e 20.000 caracteres nas instruções.");
  if (!form.model || (!model?.availableForNewUse && form.model !== currentModelId)) add("model", 2, "Selecione um modelo disponível.");
  if (!credentialOptionsForModel(credentials, models, form.model).some(item => Number(item.id) === Number(form.credentialId))) add("credentialId", 2, "Selecione uma credencial ativa compatível com o modelo.");
  if (!queues.some(item => Number(item.id) === Number(form.queueId))) add("queueId", 2, "Selecione a fila de transferência.");
  for (const parameter of ["maxTokens", "temperature"]) {
    if (!supportsAiModelParameter(model, parameter)) continue;
    const config = getAiModelParameterConfig(model, parameter);
    const number = Number(form[parameter]);
    const min = config.min ?? (parameter === "maxTokens" ? 10 : 0);
    const max = config.max ?? (parameter === "temperature" ? 1 : Infinity);
    if (parameter === "maxTokens" && !Number.isSafeInteger(number)) add(parameter, 2, "Informe um número inteiro de tokens.");
    else if (form[parameter] === "" || !Number.isFinite(number) || number < min || number > max) add(parameter, 2, `Informe um valor entre ${min} e ${Number.isFinite(max) ? max : "o limite do modelo"}.`);
  }
  if (!Number.isInteger(Number(form.maxMessages)) || Number(form.maxMessages) < 1 || Number(form.maxMessages) > 50) add("maxMessages", 2, "Use entre 1 e 50 mensagens de contexto.");
  if (!origin && form.model === VOICE_MODEL_ID && form.voice !== "texto") {
    if (!credentialOptionsForPurpose(credentials, "speech", "azure_speech").some(item => Number(item.id) === Number(form.voiceCredentialId))) add("voiceCredentialId", 2, "Selecione uma credencial de voz ativa.");
    if (!form.voiceRegion.trim()) add("voiceRegion", 2, "Informe a região do Azure Speech.");
  }
  const knowledge = form.knowledge;
  if (origin?.knowledgeRequirement === "required" && knowledge.mode !== "required") add("knowledge", 1, "Este template exige conhecimento obrigatório.");
  if (origin?.knowledgeRequirement === "none" && knowledge.mode !== "disabled") add("knowledge", 1, "Este template não utiliza uma base de conhecimento.");
  if (knowledge.mode !== "disabled") {
    const selected = knowledge.knowledgeBaseIds.map(id => bases.find(base => base.id === id));
    if (!selected.length) add("knowledge", 1, "Vincule ao menos uma base de conhecimento utilizável.");
    else if (selected.length > 20) add("knowledge", 1, "Vincule no máximo 20 bases de conhecimento.");
    else if (selected.some(base => !base?.isReady)) add("knowledge", 1, "Revise a disponibilidade das bases selecionadas antes de salvar.");
    else if (new Set(selected.map(base => base.embeddingProfileId)).size > 1) add("knowledge", 1, "As bases selecionadas precisam usar o mesmo perfil de embeddings.");
  }
  if (knowledge.mode === "required" && knowledge.fallbackAction === "continue") add("knowledge", 1, "Escolha como agir quando não houver evidência.");
  if (knowledge.mode !== "disabled" && knowledge.fallbackAction === "handoff" && !queues.some(item => Number(item.id) === Number(knowledge.fallbackQueueId))) add("knowledge", 1, "Selecione a fila de encaminhamento quando não houver evidência.");
  return errors;
};

export const agentEditorPayload = (form, origin) => {
  const common = {
    name: form.name.trim(), model: form.model, credentialId: Number(form.credentialId),
    queueId: Number(form.queueId), maxTokens: Number(form.maxTokens),
    temperature: Number(form.temperature), maxMessages: Number(form.maxMessages),
    knowledge: buildKnowledgePayload(form.knowledge),
    promptDocument: form.promptDocument,
  };
  if (origin && form.promptDocument) return common;
  if (origin) return {
    ...common, values: form.values, instructionsMode: form.instructionsMode,
    customInstructions: form.instructionsMode === "custom" ? form.customInstructions : undefined,
    confirmReplaceCustomInstructions: form.confirmReplaceCustomInstructions,
  };
  const voice = form.model === VOICE_MODEL_ID && form.voice !== "texto";
  return buildAiEditorPayload({
    ...common, prompt: form.promptDocument?.text || form.prompt.trim(), voice: voice ? form.voice : "texto",
    voiceCredentialId: voice ? Number(form.voiceCredentialId) : null,
    voiceRegion: voice ? form.voiceRegion.trim() : "",
  });
};
